import { cn } from "../../../lib/utils";

/**
 * Transparent host-DOM plate mounted ABOVE an HTML card's iframe whenever the
 * iframe does not own the pointer. This is the canvas-panning fix:
 * `pointer-events: none` on the iframe is only a main-thread hit-test
 * primitive — wheel/trackpad input can still be routed to the opaque-origin
 * frame's own (out-of-process) document, where it can never bubble back to
 * React Flow's pane listeners. No `nowheel`/`nopan` class can compensate,
 * because those are only consulted by a listener that never fires. With the
 * shield as the topmost hit target, every wheel/pointer event stays in the
 * host document, so pan/zoom/drag/marquee behave as over any other node.
 *
 * Same overlay idiom as TextNoteNode/TextLabelNode (overlay removed while
 * editing). Deliberately carries NO handlers and NO `nodrag`/`nowheel`
 * classes: events bubble to the card root and React Flow, which is the point.
 *
 * `hintLabel` is the capability-aware double-click affordance: a hover pill
 * announcing what the gesture will do on THIS block ("Double-click to
 * interact" / "…to edit text" / "…to open"), so the routing never depends on
 * an invisible flag.
 */
export function HtmlBlockShield({ hintLabel }: { hintLabel?: string | null }) {
  return (
    <div
      data-cohesive-block-shield=""
      className={cn("absolute inset-0 z-[1]", hintLabel && "cursor-pointer")}
    >
      {hintLabel && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          {/* Blur only for fine pointers — on touch devices these always-in-DOM
              backdrop-filter layers (one per block) tax WebKit's compositor on
              big canvases; the /90 background alone stays legible. */}
          <span className="whitespace-nowrap rounded-full border border-n-10 bg-n-00/90 px-2.5 py-0.5 text-[11px] font-medium text-n-80 shadow-elev-5 pointer-fine:backdrop-blur-sm">
            {hintLabel}
          </span>
        </div>
      )}
    </div>
  );
}
